<script setup>
import { computed } from "vue";

import { quickstartButtonDismissed, quickstartOpened } from "@/analytics/events";
import { trackEvent } from "@/analytics/track";

import SvgIcon from "../SvgIcon.vue";
import { useQuickStart } from "./useQuickStart";

const {
  ready,
  canSeeQuickStart,
  sidebarOpen,
  buttonDismissed,
  setSidebarOpen,
  setButtonDismissed,
} = useQuickStart();

const visible = computed(
  () => ready.value && canSeeQuickStart.value && !sidebarOpen.value && !buttonDismissed.value,
);

function openFromFab() {
  setSidebarOpen(true);
  trackEvent(quickstartOpened("fab"));
}

function dismissFab() {
  setButtonDismissed(true);
  trackEvent(quickstartButtonDismissed());
}
</script>

<template>
  <div v-if="visible" class="quick-start-button">
    <button type="button" class="trigger" @click="openFromFab">
      <SvgIcon name="system/quickstart" width="20" height="20" />
      <span>Quickstart</span>
    </button>
    <button type="button" class="dismiss" aria-label="Dismiss Quickstart" @click="dismissFab">
      <SvgIcon name="system/close" width="14" height="14" />
    </button>
  </div>
</template>

<style lang="scss" scoped>
@use "sass:color";
@use "@/colors" as c;
@use "@/styles/palette" as p;
@use "@/styles/mixins/typography";

.quick-start-button {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 40px;
  padding: 0 7px 0 0;
  border-radius: 20px;
  background-color: c.$blue;
  color: c.$white;
  box-shadow: 0 2px 12px p.$black-alpha-10;
  transition: background-color 0.15s ease;

  &:has(.trigger:hover) {
    background-color: color.adjust(c.$blue, $lightness: -8%);
  }

  &:has(.trigger:active) {
    background-color: color.adjust(c.$blue, $lightness: -14%);
  }
}

.trigger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 100%;
  padding: 0 6px 0 16px;
  border: none;
  background: none;
  color: inherit;
  cursor: pointer;

  @include typography.medium;
}

.dismiss {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background-color: p.$white-alpha-20;
  color: inherit;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: p.$white-alpha-30;
  }

  &:active {
    background-color: p.$white-alpha-40;
  }
}
</style>
